import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SUPPORTED_CURRENCIES } from '@/lib/currency';
import {
  Sparkles, X, ChevronRight, ChevronLeft, Wallet, Repeat,
  Check, Home, Wifi, Phone, Tv2, ShieldCheck, Zap, Dumbbell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
  { key: 'rent',      label: 'Alquiler',  icon: Home,        category: 'Vivienda',        description: 'Alquiler',          day: 5 },
  { key: 'internet',  label: 'Internet',  icon: Wifi,        category: 'Servicios',       description: 'Internet',          day: 10 },
  { key: 'phone',     label: 'Teléfono',  icon: Phone,       category: 'Servicios',       description: 'Plan móvil',        day: 12 },
  { key: 'streaming', label: 'Streaming', icon: Tv2,         category: 'Entretenimiento', description: 'Netflix / Spotify', day: 15 },
  { key: 'insurance', label: 'Seguro',    icon: ShieldCheck, category: 'Salud',           description: 'Seguro',            day: 8 },
  { key: 'utilities', label: 'Servicios', icon: Zap,         category: 'Servicios',       description: 'Luz / Agua / Gas',  day: 20 },
  { key: 'gym',       label: 'Gimnasio',  icon: Dumbbell,    category: 'Salud',           description: 'Gimnasio',          day: 1 },
];

const ACCOUNT_TEMPLATES = [
  { key: 'cash',       label: 'Efectivo',           type: 'cash',           name: 'Efectivo' },
  { key: 'bank',       label: 'Cuenta corriente',   type: 'bank',           name: 'Banco' },
  { key: 'credit',     label: 'Tarjeta de crédito', type: 'credit_card',    name: 'Tarjeta' },
  { key: 'wallet',     label: 'Billetera digital',  type: 'digital_wallet', name: 'Pix / Wallet' },
];

/**
 * First-run onboarding wizard. Mounted by the Dashboard.
 * Shown only when /api/onboarding/status returns needs_onboarding=true.
 */
export default function OnboardingModal({ initialCurrency = 'BRL', initialLocale = 'pt-BR', onDone }) {
  const [step, setStep] = useState(1);
  const [currency, setCurrency] = useState(initialCurrency);
  const [accounts, setAccounts] = useState([]); // array of {type,name,initial_balance,credit_limit,statement_closing_day,payment_due_day}
  const [presets, setPresets] = useState({});   // {presetKey: amount}
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState('');

  // Pre-select cash + bank by default for quick setup
  useEffect(() => {
    setAccounts([
      { type: 'cash', name: 'Efectivo', initial_balance: 0 },
      { type: 'bank', name: 'Banco', initial_balance: 0 },
    ]);
  }, []);

  const toggleAccount = (tpl) => {
    setAccounts(prev => {
      const exists = prev.find(a => a.type === tpl.type && a.name === tpl.name);
      if (exists) return prev.filter(a => !(a.type === tpl.type && a.name === tpl.name));
      const base = { type: tpl.type, name: tpl.name, initial_balance: 0 };
      if (tpl.type === 'credit_card') {
        base.credit_limit = 5000;
        base.statement_closing_day = 15;
        base.payment_due_day = 22;
      }
      return [...prev, base];
    });
  };

  const updateAccountBalance = (idx, value) => {
    setAccounts(prev => prev.map((a, i) =>
      i === idx ? { ...a, initial_balance: value === '' ? 0 : parseFloat(value) } : a
    ));
  };

  const togglePreset = (key) => {
    setPresets(prev => {
      const next = { ...prev };
      if (key in next) delete next[key];
      else next[key] = '';
      return next;
    });
  };

  const updatePresetAmount = (key, value) => {
    setPresets(prev => ({ ...prev, [key]: value }));
  };

  const skip = async () => {
    setSkipping(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/onboarding/skip`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      onDone?.({ skipped: true });
    } finally {
      setSkipping(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const meta = SUPPORTED_CURRENCIES.find(c => c.code === currency);
      const recurring = Object.entries(presets)
        .map(([key, amount]) => {
          const preset = PRESETS.find(p => p.key === key);
          const amt = parseFloat(amount);
          if (!preset || !amt || amt <= 0) return null;
          return {
            description: preset.description,
            category: preset.category,
            amount: amt,
            day_of_month: preset.day,
          };
        })
        .filter(Boolean);

      const res = await fetch(`${API_URL}/api/onboarding/seed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accounts,
          recurring,
          currency,
          locale: meta?.locale,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'No se pudo guardar la configuración inicial');
        return;
      }
      onDone?.(data);
    } catch (err) {
      setError(err.message || 'Error inesperado');
    } finally {
      setSaving(false);
    }
  };

  const stepCount = 3;

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" role="dialog" aria-label="Onboarding" data-testid="onboarding-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 flex items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
            <Sparkles className="h-6 w-6 text-violet-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-white">¡Bienvenido a WSFinances!</h2>
            <p className="text-zinc-400 text-sm">Configuremos lo básico en menos de un minuto.</p>
          </div>
          <button
            onClick={skip}
            disabled={skipping}
            className="text-zinc-400 hover:text-white p-1 -mr-1"
            aria-label="Saltar"
            data-testid="onboarding-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-3 border-b border-zinc-800 flex items-center gap-2">
          {[1, 2, 3].map(n => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                n < step ? 'bg-emerald-500 text-white' :
                n === step ? 'bg-violet-500 text-white' :
                'bg-zinc-800 text-zinc-500'
              }`}>
                {n < step ? <Check className="h-4 w-4" /> : n}
              </div>
              <span className={`text-xs ${n === step ? 'text-white' : 'text-zinc-500'}`}>
                {n === 1 ? 'Moneda' : n === 2 ? 'Cuentas' : 'Recurrentes'}
              </span>
              {n < stepCount && <div className="flex-1 h-px bg-zinc-800" />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="p-6 space-y-4">
          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <h3 className="text-lg font-semibold text-white mb-2">¿Cuál es tu moneda principal?</h3>
              <p className="text-sm text-zinc-400 mb-4">
                Se usará para formatear todos los montos. Puedes cambiarla luego en Configuración.
              </p>
              <Label htmlFor="ob-currency">Moneda</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger id="ob-currency" className="bg-zinc-950 border-zinc-800 mt-2" data-testid="onboarding-currency-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CURRENCIES.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="inline-block w-12 font-mono">{c.symbol}</span>
                      <span className="ml-2">{c.label} ({c.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-2">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <Wallet className="h-5 w-5 text-violet-400" /> ¿Qué cuentas tienes?
              </h3>
              <p className="text-sm text-zinc-400 mb-4">
                Selecciona los tipos que usas. Podrás añadir más o renombrarlas luego.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {ACCOUNT_TEMPLATES.map(tpl => {
                  const isSelected = accounts.some(a => a.type === tpl.type && a.name === tpl.name);
                  return (
                    <button
                      key={tpl.key}
                      type="button"
                      onClick={() => toggleAccount(tpl)}
                      className={`px-3 py-3 rounded-lg border text-left text-sm transition-colors ${
                        isSelected
                          ? 'bg-violet-500/15 border-violet-500/50 text-violet-100'
                          : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800'
                      }`}
                      data-testid={`onboarding-acc-${tpl.key}`}
                    >
                      <p className="font-medium">{tpl.label}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{tpl.name}</p>
                    </button>
                  );
                })}
              </div>

              {accounts.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-zinc-800">
                  <p className="text-xs text-zinc-400">Saldo inicial (opcional)</p>
                  {accounts.map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-sm text-zinc-300 flex-1 truncate">{a.name}</span>
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={a.initial_balance === 0 ? '' : a.initial_balance}
                        onChange={(e) => updateAccountBalance(idx, e.target.value)}
                        placeholder="0.00"
                        className="bg-zinc-950 border-zinc-800 h-8 w-32 text-right text-sm"
                        data-testid={`onboarding-balance-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-3">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <Repeat className="h-5 w-5 text-violet-400" /> ¿Qué pagos recurrentes tienes?
              </h3>
              <p className="text-sm text-zinc-400 mb-4">
                Elige los que aplican y pon el monto. Te enviaremos recordatorios antes del vencimiento.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {PRESETS.map(p => {
                  const Icon = p.icon;
                  const selected = p.key in presets;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => togglePreset(p.key)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        selected
                          ? 'bg-violet-500/15 border-violet-500/50 text-violet-100'
                          : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800'
                      }`}
                      data-testid={`onboarding-preset-${p.key}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span className="text-sm font-medium">{p.label}</span>
                      </div>
                      <span className="text-[10px] text-zinc-500">día {p.day}</span>
                    </button>
                  );
                })}
              </div>

              {Object.keys(presets).length > 0 && (
                <div className="space-y-2 pt-2 border-t border-zinc-800">
                  <p className="text-xs text-zinc-400">Monto mensual estimado</p>
                  {Object.keys(presets).map(k => {
                    const p = PRESETS.find(x => x.key === k);
                    return (
                      <div key={k} className="flex items-center gap-2">
                        <span className="text-sm text-zinc-300 flex-1 truncate">{p?.label}</span>
                        <Input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={presets[k]}
                          onChange={(e) => updatePresetAmount(k, e.target.value)}
                          placeholder="0.00"
                          className="bg-zinc-950 border-zinc-800 h-8 w-32 text-right text-sm"
                          data-testid={`onboarding-preset-amount-${k}`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="ghost"
            className="text-zinc-400 hover:text-white"
            onClick={skip}
            disabled={skipping || saving}
            data-testid="onboarding-skip"
          >
            Saltar
          </Button>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button
                variant="outline"
                className="border-zinc-700 hover:bg-zinc-800 text-zinc-200"
                onClick={() => setStep(step - 1)}
                disabled={saving}
                data-testid="onboarding-back"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Atrás
              </Button>
            )}
            {step < stepCount ? (
              <Button
                className="bg-violet-600 hover:bg-violet-700"
                onClick={() => setStep(step + 1)}
                data-testid="onboarding-next"
              >
                Siguiente <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                className="bg-violet-600 hover:bg-violet-700"
                onClick={finish}
                disabled={saving}
                data-testid="onboarding-finish"
              >
                {saving ? 'Guardando…' : <>¡Terminar! <Check className="h-4 w-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
